import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ChevronLeft, LogOut, Heart, Users, Send, Clock, Stethoscope, Phone, AlertTriangle,
  Droplets, User, Mail, Calendar, CalendarDays, Brain, Star, FileText, Bus, Waves, Shirt, ExternalLink
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatPhone(phone) {
  if (!phone) return "";
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === "1") return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return phone;
}

function CamperFullDetail({ camper, notes, onAddNote, noteLoading }) {
  const [noteText, setNoteText] = useState("");
  const medicalData = camper.medical_data || {};
  const parentInfo = camper.parent_info || {};
  const display = camper.preferred_name || camper.first_name;
  const initials = `${camper.first_name?.[0] || ''}${camper.last_name?.[0] || ''}`.toUpperCase();

  const handleSubmit = () => {
    if (!noteText.trim()) return;
    onAddNote(noteText.trim());
    setNoteText("");
  };

  const sectionOrder = ["Medical", "Family", "Behavioral", "Additional Info", "Skippers Only", "Transportation", "Swim"];
  const sectionIcons = { "Medical": Stethoscope, "Family": Heart, "Behavioral": Brain, "Additional Info": Star, "Skippers Only": FileText, "Transportation": Bus, "Swim": Waves };
  const sectionColors = { "Medical": "text-red-700", "Family": "text-blue-700", "Behavioral": "text-purple-700", "Additional Info": "text-green-700", "Skippers Only": "text-orange-700", "Transportation": "text-teal-700", "Swim": "text-cyan-700" };
  const hasSections = typeof Object.values(medicalData)[0] === 'object';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center flex-shrink-0">
          <span className="font-bold text-teal-700 text-xl">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-xl text-teal-800">{display} {camper.last_name}</h2>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {camper.group_code && (
              <Badge variant="secondary" className="bg-teal-50 text-teal-700 text-xs">
                <Users className="w-3 h-3 mr-1" />{camper.cit_subgroup || camper.group_name || camper.group_code}
              </Badge>
            )}
            {camper.age && <Badge variant="outline" className="text-xs"><Calendar className="w-3 h-3 mr-1" />Age {Math.floor(camper.age)}</Badge>}
            {camper.gender && <Badge variant="outline" className="text-xs"><User className="w-3 h-3 mr-1" />{camper.gender}</Badge>}
            {camper.date_of_birth && (
              <Badge variant="outline" className="text-xs"><Calendar className="w-3 h-3 mr-1" />{new Date(camper.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}</Badge>
            )}
            {camper.years_at_camp != null && (
              <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">{camper.years_at_camp} {camper.years_at_camp === 1 ? 'Year' : 'Years'} at Camp</Badge>
            )}
            {camper.previous_group && (
              <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">Prev: {camper.previous_group}</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Parent / Guardian */}
      {(parentInfo.name || parentInfo.phone || parentInfo.email) && (
        <section>
          <h3 className="font-semibold text-base flex items-center gap-2 mb-3 text-blue-700">
            <User className="w-4 h-4" />Parent / Guardian
          </h3>
          <div className="grid gap-2">
            {parentInfo.name && (
              <div className="flex items-center gap-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="min-w-0"><p className="text-xs text-muted-foreground">Name</p><p className="text-sm font-medium truncate">{parentInfo.name}</p></div>
              </div>
            )}
            {parentInfo.phone && (
              <div className="flex items-center gap-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="min-w-0"><p className="text-xs text-muted-foreground">Phone</p><p className="text-sm font-medium">{formatPhone(parentInfo.phone)}</p></div>
              </div>
            )}
            {parentInfo.email && (
              <div className="flex items-center gap-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="min-w-0"><p className="text-xs text-muted-foreground">Email</p><p className="text-sm font-medium break-all">{parentInfo.email}</p></div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Sessions / Program */}
      {camper.sessions && camper.sessions.length > 0 && (
        <section>
          <h3 className="font-semibold text-base flex items-center gap-2 mb-3 text-emerald-700">
            <CalendarDays className="w-4 h-4" />Program
          </h3>
          <div className="grid gap-2">
            {camper.sessions.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <span className="text-sm font-medium text-emerald-800">{s.name}</span>
                <span className="text-xs text-emerald-600">
                  {new Date(s.start + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })} – {new Date(s.end + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Flex Schedule */}
      {camper.is_flex && camper.flex_days && camper.flex_days.length > 0 && (
        <section>
          <h3 className="font-semibold text-base flex items-center gap-2 mb-3 text-emerald-700">
            <CalendarDays className="w-4 h-4" />Flex Schedule ({camper.flex_days.length} days)
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {camper.flex_days.map(day => {
              const d = new Date(day + "T12:00:00");
              const label = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' });
              return <Badge key={day} variant="secondary" className="text-xs">{label}</Badge>;
            })}
          </div>
        </section>
      )}

      {/* Full Medical Sections (same as Admin) */}
      {hasSections ? sectionOrder.map(section => {
        const data = medicalData[section];
        if (!data || Object.keys(data).length === 0) return null;
        if (section === "Skippers Only") {
          const code = (camper.group_code || "").toUpperCase();
          const num = parseInt(code.replace(/\D/g, ''), 10);
          if (!code.startsWith('N') || num < 2 || num > 16) return null;
        }
        const Icon = sectionIcons[section] || FileText;
        const color = sectionColors[section] || "text-teal-700";
        return (
          <section key={section}>
            <h3 className={`font-semibold text-base flex items-center gap-2 mb-3 ${color}`}>
              <Icon className="w-4 h-4" />{section}
            </h3>
            <div className="grid gap-2">
              {Object.entries(data)
                .sort((a, b) => {
                  if (section === "Transportation") {
                    const order = ["Bus# AM", "Trans-AM Drop Off Method", "Bus# PM", "Trans-PM Dismissal Method"];
                    return (order.indexOf(a[0]) === -1 ? 99 : order.indexOf(a[0])) - (order.indexOf(b[0]) === -1 ? 99 : order.indexOf(b[0]));
                  }
                  return 0;
                })
                .map(([key, value]) => {
                  const isAlert = key.toLowerCase().includes('allerg') || key.toLowerCase().includes('meds') || key.toLowerCase().includes('reaction');
                  return (
                    <div key={key} className={`p-3 rounded-lg border ${isAlert && section === "Medical" ? 'bg-red-50 border-red-200' : 'bg-muted/30'}`}>
                      <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${isAlert && section === "Medical" ? 'text-red-600' : 'text-muted-foreground'}`}>
                        {isAlert && section === "Medical" && <AlertTriangle className="w-3 h-3 inline mr-1" />}{key}
                      </p>
                      <p className={`text-sm ${isAlert && section === "Medical" ? 'text-red-800 font-medium' : ''}`}>{value || "—"}</p>
                    </div>
                  );
                })}
            </div>
          </section>
        );
      }) : Object.keys(medicalData).length > 0 ? (
        <section>
          <h3 className="font-semibold text-base flex items-center gap-2 mb-3 text-red-700">
            <Stethoscope className="w-4 h-4" />Medical Information
          </h3>
          <div className="grid gap-2">
            {Object.entries(medicalData).map(([key, value]) => (
              <div key={key} className="p-3 rounded-lg border bg-muted/30">
                <p className="text-xs font-medium uppercase tracking-wider mb-1 text-muted-foreground">{key}</p>
                <p className="text-sm">{value || "—"}</p>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <section>
          <h3 className="font-semibold text-base flex items-center gap-2 mb-3 text-red-700">
            <Stethoscope className="w-4 h-4" />Medical Information
          </h3>
          <div className="text-center py-6 bg-muted/30 rounded-lg">
            <Stethoscope className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No medical data on file</p>
          </div>
        </section>
      )}

      {/* NURSE NOTES */}
      <div className="pt-4 border-t-2 border-teal-200">
        <h3 className="text-sm font-bold text-teal-800 flex items-center gap-2 mb-3">
          <Stethoscope className="w-4 h-4" /> Nurse Notes ({notes.length})
        </h3>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            placeholder="Add a nurse note..."
            className="flex-1 h-9 px-3 rounded-lg border border-teal-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
            data-testid="health-note-input"
          />
          <Button size="sm" onClick={handleSubmit} disabled={noteLoading || !noteText.trim()} className="bg-teal-600 hover:bg-teal-700" data-testid="health-note-submit">
            <Send className="w-4 h-4" />
          </Button>
        </div>
        {notes.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">No nurse notes yet</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {notes.map((n, i) => (
              <div key={i} className="bg-teal-50 rounded-lg px-3 py-2 border border-teal-100" data-testid={`health-note-${i}`}>
                <p className="text-sm">{n.note}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Clock className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(n.timestamp).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </span>
                  <span className="text-[10px] text-teal-600 font-medium">{n.added_by}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Metadata */}
      <section className="text-xs text-muted-foreground border-t pt-4">
        <p>Person ID: {camper.person_id}</p>
        {camper.date_of_birth && <p>DOB: {camper.date_of_birth}</p>}
        {camper.last_synced && <p>Last Synced: {new Date(camper.last_synced).toLocaleString("en-US", { timeZone: "America/New_York" })}</p>}
      </section>
    </div>
  );
}

export default function HealthDashboard({ onLogout }) {
  const [view, setView] = useState("menu"); // menu, groups, campers, detail
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [campers, setCampers] = useState([]);
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/health/groups`, { headers });
      setGroups(res.data);
    } catch { toast.error("Failed to load groups"); }
    finally { setLoading(false); }
  }, []);

  const fetchCampers = async (groupCode) => {
    setLoading(true);
    setSelectedGroup(groupCode);
    setView("campers");
    try {
      const res = await axios.get(`${API}/health/group/${groupCode}`, { headers });
      setCampers(res.data);
    } catch { toast.error("Failed to load campers"); }
    finally { setLoading(false); }
  };

  const openCamperDetail = async (camper) => {
    setSelectedCamper(camper);
    setView("detail");
    try {
      const res = await axios.get(`${API}/health/notes/${camper.id}`, { headers });
      setNotes(res.data);
    } catch { setNotes([]); }
  };

  const addNote = async (noteText) => {
    setNoteLoading(true);
    try {
      await axios.post(`${API}/health/notes`, { camper_id: selectedCamper.id, note: noteText }, { headers });
      toast.success("Note added");
      const res = await axios.get(`${API}/health/notes/${selectedCamper.id}`, { headers });
      setNotes(res.data);
    } catch { toast.error("Failed to add note"); }
    finally { setNoteLoading(false); }
  };

  const goBack = () => {
    if (view === "detail") { setView("campers"); setSelectedCamper(null); setNotes([]); }
    else if (view === "campers") { setView("groups"); setSelectedGroup(null); setCampers([]); }
    else if (view === "groups") { setView("menu"); }
  };

  // MENU
  if (view === "menu") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50/40 to-white">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-teal-600" />
            <div>
              <h1 className="text-sm font-bold text-teal-800">Health Center</h1>
              <p className="text-[10px] text-muted-foreground">Nurse Dashboard</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="health-logout">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </header>
        <div className="px-4 py-8 max-w-lg mx-auto space-y-3">
          <button
            onClick={() => { setView("groups"); fetchGroups(); }}
            className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 bg-teal-50 border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all active:scale-[0.98]"
            data-testid="health-card-campers"
          >
            <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center border border-teal-200">
              <Users className="w-6 h-6 text-teal-600" />
            </div>
            <div className="text-left">
              <span className="text-sm font-bold text-teal-800">Campers</span>
              <p className="text-[10px] text-muted-foreground">View all groups, camper info & add nurse notes</p>
            </div>
          </button>
          <a
            href="https://rrdcstaffsnapshot.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 bg-emerald-50 border-emerald-200 hover:border-emerald-400 hover:shadow-lg transition-all active:scale-[0.98]"
            data-testid="health-card-staff-snapshot"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center border border-emerald-200">
              <Shirt className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="text-left flex-1 flex items-center justify-between">
              <div>
                <span className="text-sm font-bold text-emerald-800">Staff Snapshot</span>
                <p className="text-[10px] text-muted-foreground">rrdcstaffsnapshot.com</p>
              </div>
              <ExternalLink className="w-4 h-4 text-emerald-600" />
            </div>
          </a>
        </div>
      </div>
    );
  }

  // GROUPS LIST
  if (view === "groups") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50/40 to-white">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={goBack} data-testid="health-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Menu
            </Button>
            <h1 className="text-sm font-bold text-teal-800">All Groups</h1>
          </div>
        </header>
        <div className="px-4 py-4">
          {loading ? (
            <div className="text-center py-10 text-muted-foreground">Loading groups...</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {groups.map(g => (
                <button
                  key={g.group_code}
                  onClick={() => fetchCampers(g.group_code)}
                  className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 bg-white border-gray-200 hover:border-teal-300 hover:shadow-md transition-all active:scale-95"
                  data-testid={`health-group-${g.group_code}`}
                >
                  <span className="text-sm font-bold">{g.group_code}</span>
                  <span className="text-[10px] text-muted-foreground">{g.camper_count} campers</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // CAMPERS LIST
  if (view === "campers") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50/40 to-white">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={goBack} data-testid="health-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Groups
            </Button>
            <h1 className="text-sm font-bold text-teal-800">{selectedGroup} — {campers.length} Campers</h1>
          </div>
        </header>
        <div className="px-4 py-4 space-y-2">
          {loading ? (
            <div className="text-center py-10 text-muted-foreground">Loading campers...</div>
          ) : campers.map(c => {
            const display = c.first_name;
            const md = c.medical_data?.Medical || {};
            const hasAllergy = md["Allergies/Food"] && !["no", "none", "n/a", ""].includes((md["Allergies/Food"] || "").toLowerCase());
            return (
              <Card
                key={c.id}
                className={`cursor-pointer hover:shadow-md transition-all ${hasAllergy ? "border-amber-200" : ""}`}
                onClick={() => openCamperDetail(c)}
                data-testid={`health-camper-${c.id}`}
              >
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-sm">
                      {(display || "?")[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{display} {c.last_name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span>Age {c.age}</span>
                        {hasAllergy && <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700 px-1 py-0">Allergies</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.health_notes_count > 0 && (
                      <Badge className="bg-teal-100 text-teal-700 text-[10px]">
                        <Stethoscope className="w-3 h-3 mr-0.5" />{c.health_notes_count}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // CAMPER DETAIL
  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/40 to-white">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b px-4 py-3 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={goBack} data-testid="health-back">
          <ChevronLeft className="w-4 h-4 mr-1" /> {selectedGroup}
        </Button>
        <h1 className="text-sm font-bold text-teal-800">Camper Detail</h1>
      </header>
      <div className="px-4 py-4 max-w-2xl mx-auto">
        {selectedCamper && (
          <CamperFullDetail camper={selectedCamper} notes={notes} onAddNote={addNote} noteLoading={noteLoading} />
        )}
      </div>
    </div>
  );
}
